/**
 * React Query hooks for CRM companies.
 *
 * Replaces the prior useState/useEffect implementation. Server state
 * caches in `@tanstack/react-query` so switching tabs no longer
 * refetches and mutations invalidate cleanly. Query keys come from
 * `lib/query-keys.ts` (`queryKeys.crm.companies.*`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { CrmCreateCompanyInput, CrmListCompaniesResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseCompaniesOptions {
  search?: string;
  industry?: string;
  status?: 'active' | 'archived' | 'deleted';
  sortBy?: string;
  sortDir?: string;
  limit?: number;
  offset?: number;
}

function buildParams(options: UseCompaniesOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.search) params.search = options.search;
  if (options.industry) params.industry = options.industry;
  if (options.status) params.status = options.status;
  if (options.sortBy) params.sortBy = options.sortBy;
  if (options.sortDir) params.sortDir = options.sortDir;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  return params;
}

export function useCompanies(options: UseCompaniesOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<CrmListCompaniesResult>({
    queryKey: queryKeys.crm.companies.list(filters),
    queryFn: () => api.listCrmCompanies(filters),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.companies.all() });

  const createMutation = useMutation({
    mutationFn: (input: CrmCreateCompanyInput) => api.createCrmCompany(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create company', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CrmCreateCompanyInput> }) =>
      api.updateCrmCompany(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update company', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCrmCompany(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete company', variant: 'destructive' }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.archiveCrmCompany(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to archive company', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    // Surface the query error once so callers see the toast even when
    // their callbacks don't trigger.
    toast({ title: 'Error', description: 'Failed to load companies', variant: 'destructive' });
  }

  return {
    companies: query.data?.companies ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createCompany: (input: CrmCreateCompanyInput) => createMutation.mutateAsync(input),
    updateCompany: (id: string, input: Partial<CrmCreateCompanyInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteCompany: (id: string) => deleteMutation.mutateAsync(id),
    archiveCompany: (id: string) => archiveMutation.mutateAsync(id),
  };
}
